import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import httpClient from "../../utils/httpClient";
import { artifactAPIDataType } from "../../pages/CTProductCostSetup/ProductDataType";

interface RequestDetails {
  productId: string;
}

interface RequestDetailsFile {
  productId: string;
  productArtifactId: string;
}

interface ArtifactDetailsState {
  isLoading: boolean;
  data: {
    Code: number;
    Status: string;
    Message: string;
    Data: artifactAPIDataType[] | null;
  } | null;
  error: string | null;
}

const initialState: ArtifactDetailsState = {
  isLoading: true,
  data: null,
  error: null,
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const fetchArtifactDetailsData = createAsyncThunk<any, RequestDetails>(
  "CTArtifactDetailsSlice/fetchArtifactDetailsData",
  async (requestDetails, thunkApi) => {
    try {
      const response = await httpClient.post("/product/artifact", {
        ProductId: requestDetails.productId,
        Action: "Get",
      });

      if (response.status != 200 || response.data.Status !== "success") {
        if (response.data.Status == "error") {
          return thunkApi.rejectWithValue({
            message: response.data.Message,
          });
        } else {
          return thunkApi.rejectWithValue({
            message: "Failed to get product details",
          });
        }
      }
      return response.data;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      console.log("🚀 ~ error:", error);
      return thunkApi.rejectWithValue({
        message: "Failed to get product details",
      });
    }
  },
);

export const fetchArtifactFileData = createAsyncThunk<any, RequestDetailsFile>(
  "CTArtifactDetailsSlice/fetchArtifactFileData",
  async (requestDetails, thunkApi) => {
    try {
      const response = await httpClient.post("/product/artifact", {
        ProductId: requestDetails.productId,
        ProductArtifactId: requestDetails.productArtifactId,
        Action: "Download",
      });

      if (response.status != 200 || response.data.Status !== "success") {
        if (response.data.Status == "error") {
          return thunkApi.rejectWithValue({
            message: response.data.Message,
          });
        } else {
          return thunkApi.rejectWithValue({
            message: "Failed to get product details",
          });
        }
      }
      return response.data;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      console.log("🚀 ~ error:", error);
      return thunkApi.rejectWithValue({
        message: "Failed to get product details",
      });
    }
  },
);

interface RequestDetailsFileDelete {
  productId: string | undefined;
  productArtifactId: string | undefined;
  ResourceType: string | undefined;
}

export const deleteArtifactFileData = createAsyncThunk<any, RequestDetailsFileDelete>(
  "CTArtifactDetailsSlice/deleteArtifactFileData",
  async (requestDetails, thunkApi) => {
    try {
      const response = await httpClient.post("/db/product/artifact", {
        ProductId: requestDetails.productId,
        ProductArtifactId: requestDetails.productArtifactId,
        ResourceType: requestDetails.ResourceType,
        Action: "Delete",
      });

      if (response.status != 200 || response.data.Status !== "success") {
        if (response.data.Status == "error") {
          return thunkApi.rejectWithValue({
            message: response.data.Message,
          });
        } else {
          return thunkApi.rejectWithValue({
            message: "Failed to delete artifact",
          });
        }
      }
      return response.data;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      console.log("🚀 ~ error:", error);
      return thunkApi.rejectWithValue({
        message: "Failed to delete artifact",
      });
    }
  },
);


const CTArtifactDetailsSlice = createSlice({
  name: "CTArtifactDetailsSlice",
  initialState,
  reducers: {
    resetArtifactDetailState: (state) => {
      state.isLoading = false;
      state.data = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchArtifactDetailsData.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.data = null;
      })
      .addCase(
        fetchArtifactDetailsData.fulfilled,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (state, action: PayloadAction<any>) => {
          if (action.payload.Status == "success") {
            state.isLoading = false;
            state.data = action.payload;
            state.error = null;
          } else if (action.payload.Status == "error") {
            state.isLoading = false;
            state.data = null;
            state.error = action.payload.message;
          }
        },
      )
      .addCase(
        fetchArtifactDetailsData.rejected,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (state, action: PayloadAction<any>) => {
          state.isLoading = false;
          state.data = null;
          state.error = action.payload.message;
        },
      );
  },
});

export const { resetArtifactDetailState } = CTArtifactDetailsSlice.actions;

export default CTArtifactDetailsSlice.reducer;
